import React, { useState, useRef } from 'react'
import dayjs from 'dayjs'
import styles from './CalendarItem.module.scss'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import cx from 'classnames'
import { Tag } from 'common/OptionSelect/Tag'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import { ReactComponent as PreviewUnavailableIcon } from 'screens/blaze/Home/components/Catalogue/components/FilePreview/previewUnavailable.svg'
import { RoundedPlatformIcon } from 'components/RoundedPlatformIcon/RoundedPlatformIcon'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Text } from 'blaze-ui/Text/Text'
import { Pressable } from '@react-aria/interactions'
import { generatePath, useHistory } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { useProgress } from 'screens/Calendar/WeekViewV2/CalendarEventCard/useProgress'
import { getLoadingColor } from 'screens/Calendar/WeekViewV2/utils'
import { isArticle, isFileFromLatestBatch } from 'screens/ScheduledPosts/utils'
import { DesignContentPreviewResizeWrapper } from 'components/DesignContentPreviewResizeWrapper/DesignContentPreviewResizeWrapper'
import { StoreSnapshot } from '@tldraw/tldraw'

interface CalendarItemProps {
  item: AlmanacFileCalendarItem
  brandKitColors?: string[]
}

export const STATUS_COLOR: Record<string, string> = {
  Draft: '#B1BECD', // grey-bright
  'Reminder Set': '#FFEFC5', // yellow-light
  Autoscheduled: '#FFD74A', // yellow-bright
  Posted: '#D6FFE6', // green-light
  Failed: '#FFCBCB', // red-light
}

export const STATUS_COLOR_V2: Record<string, string> = {
  Draft: '#EAEDF1',
  'Reminder Set': '#F8F0FF',
  Autoscheduled: '#FFF9EF',
  Posted: '#ECFDF3',
  Failed: '#FDECEC',
  Posting: '#FFEFC5',
  Paused: '#EAEDF1',
}

export const STATUS_FONT_COLOR_V2: Record<string, string> = {
  Draft: '#344054',
  'Reminder Set': '#A229FE',
  Autoscheduled: '#D38E0F',
  Posted: '#07942A',
  Failed: '#BC010B',
  Posting: '#D38E0F',
  Paused: '#344054',
}

export const CalendarItem: React.FC<CalendarItemProps> = ({ item, brandKitColors = [] }) => {
  const {
    post_at,
    image_url,
    status,
    duration,
    content,
    channel,
    asset_type,
    key,
    created_at,
    design_content,
    is_generating,
    image_url_generating,
    width,
    height,
  } = item

  const history = useHistory()
  const [imageLoaded, setImageLoaded] = useState(false)
  const [designLoaded, setDesignLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleDesignLoadingStateChange = (status: string) => {
    setDesignLoaded(status === 'synced-local')
  }

  const shouldShowSkeletonLoader = () => {
    if (channel === 'instagram_story') {
      return false
    }
    return true
  }

  const formattedTime = dayjs(post_at).format('h:mma')

  const isArticlePreview = isArticle({ almanac_file: item })
  const isWithinPreviewImageWaitTime = isFileFromLatestBatch(item)
  const isLoadingDocPreview = isArticlePreview && !image_url && isWithinPreviewImageWaitTime

  let isLoading = false

  if (image_url && !imageLoaded) {
    isLoading = true
  }

  if (!image_url && design_content && !designLoaded) {
    isLoading = true
  }

  if (isArticlePreview && !image_url && !design_content && isWithinPreviewImageWaitTime) {
    isLoading = true
  }

  if ((is_generating || image_url_generating) && !image_url && !design_content) {
    isLoading = true
  }

  const eventForLoading = {
    ...item,
    id: item.key,
    start: item.post_at,
    end: item.post_at,
  }

  const { progress } = useProgress({ date: created_at }) || { progress: 0 }
  const loadingColor = getLoadingColor(eventForLoading, brandKitColors)

  const handlePress = () => {
    if (asset_type === 'Design') {
      history.push(generatePath(DESIGN_PATH, { key: key }))
    } else {
      history.push(generatePath(DOC_PATH, { slug: key }))
    }
  }

  return (
    <Pressable onPress={handlePress}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.time}>{formattedTime}</div>
          {status && (
            <div>
              <Tag size="sm" color={STATUS_COLOR[status]}>
                {status}
              </Tag>
            </div>
          )}

          <div className={styles.textContent}>
            {(() => {
              if ((is_generating || image_url_generating) && shouldShowSkeletonLoader() && !content) {
                return (
                  <div className={styles.skeletonLoader}>
                    <div className={styles.skeletonLine}></div>
                    <div className={styles.skeletonLine}></div>
                    <div className={styles.skeletonLine}></div>
                  </div>
                )
              }

              if (asset_type === 'Design' && !content) {
                return (
                  <div className={styles.emptyCaptionContainer}>
                    <span className={styles.emptyCaptionText}>No caption or post copy</span>
                  </div>
                )
              }

              return (
                <Text variant="secondary" lineClamp={3}>
                  {content}
                </Text>
              )
            })()}
          </div>
        </div>

        <div
          className={cx(styles.imageContainer, {
            [styles.textPreview]: !image_url && content,
            [styles.empty]: !image_url && !design_content && !content && !isLoading && !isLoadingDocPreview,
          })}
          ref={containerRef}
        >
          {channel && (
            <div className={styles.channelIndicator}>
              <RoundedPlatformIcon platform={providerFromChannelType[channel] as ExternalIntegrationEnum} />
            </div>
          )}

          {image_url && (
            <>
              {isLoading && (
                <div
                  className={styles.loadingStateOverlay}
                  style={{ background: `linear-gradient(180deg, #f8f9fa 0%, ${loadingColor} 100%)` }}
                >
                  <div className={styles.progressBar}>
                    <div className={styles.progress} style={{ width: `${Math.min(progress, 90)}%` }} />
                  </div>
                </div>
              )}
              <img
                src={image_url}
                alt="preview"
                className={cx(styles.image, { [styles.hidden]: isLoading })}
                onLoad={() => setImageLoaded(true)}
              />
              {duration && !isLoading && <VideoDuration duration={duration} position="left" />}
            </>
          )}

          {!image_url && design_content && width && height && (
            <>
              {!designLoaded && (
                <div
                  className={styles.loadingStateOverlay}
                  style={{ background: `linear-gradient(180deg, #f8f9fa 0%, ${loadingColor} 100%)` }}
                >
                  <div className={styles.progressBar}>
                    <div className={styles.progress} style={{ width: `${Math.min(progress, 90)}%` }} />
                  </div>
                </div>
              )}
              <DesignContentPreviewResizeWrapper
                width={width}
                height={height}
                designContent={design_content as StoreSnapshot<any>}
                containerRef={containerRef}
                className={cx({ [styles.hidden]: !designLoaded })}
                onLoadingStateChange={handleDesignLoadingStateChange}
                autoplayVideo={false}
              />
            </>
          )}

          {!image_url && !design_content && content && !isLoadingDocPreview && (
            <div className={styles.textPreviewContainer}>
              <div className={styles.textPreview}>{content}</div>
            </div>
          )}

          {!image_url && !design_content && (isLoading || isLoadingDocPreview) && (
            <div
              className={styles.loadingStateOverlay}
              style={{ background: `linear-gradient(180deg, #f8f9fa 0%, ${loadingColor} 100%)` }}
            >
              <div className={styles.progressBar}>
                <div className={styles.progress} style={{ width: `${Math.min(progress, 90)}%` }} />
              </div>
            </div>
          )}

          {!image_url && !design_content && !content && !isLoading && !isLoadingDocPreview && (
            <PreviewUnavailableIcon />
          )}
        </div>
      </div>
    </Pressable>
  )
}
